import { useState, useCallback, useEffect } from 'react';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, jobService, paymentService } from '../services/api';
import type { PaymentReprocessResult } from '../services/api';
import { JobStatus, PaymentStatus } from '../types';
import type { JobSummaryDto, CarrierEarningsDashboardDto, MonthlyStatementDto } from '../types';
import type { AdminPaymentsQueueDto } from '../types/modules';

interface UserDashboardData {
  fullName: string;
  role: string;
  availableBalance: number;
  lockedBalance: number;
  pendingBalance: number;
  referralEarnings: number;
  cashbackCredit: number;
  activeJobs: number;
  completedJobs: number;
}

const PAY_STATUS_LABELS: Record<number, string> = {
  [PaymentStatus.Pending]:    'Pending',
  [PaymentStatus.Processing]: 'Processing',
  [PaymentStatus.Released]:   'Released',
  [PaymentStatus.Failed]:     'Failed',
  [PaymentStatus.Refunded]:   'Refunded',
  [PaymentStatus.OnHold]:     'On Hold',
};

const PAY_STATUS_STYLE: Record<number, React.CSSProperties> = {
  [PaymentStatus.Pending]:    { background: '#fef3c7', color: '#92400e' },
  [PaymentStatus.Processing]: { background: '#dbeafe', color: '#1d4ed8' },
  [PaymentStatus.Released]:   { background: '#dcfce7', color: '#166534' },
  [PaymentStatus.Failed]:     { background: '#fee2e2', color: '#991b1b' },
  [PaymentStatus.Refunded]:   { background: '#f3e8ff', color: '#7c3aed' },
  [PaymentStatus.OnHold]:     { background: '#f3f4f6', color: '#374151' },
};

type ExpandedCard = 'available' | 'locked' | 'pending' | 'referral' | 'cashback' | 'cod' | 'platformFee' | 'pendingFee' | null;
type PageTab = 'dashboard' | 'statement';

interface BalanceCardProps {
  icon: string;
  label: string;
  amount: number;
  color: string;
  bg: string;
  hint: string;
  expandKey: ExpandedCard;
  expanded: ExpandedCard;
  onToggle: (key: ExpandedCard) => void;
  children?: React.ReactNode;
  debit?: boolean;
}

function BalanceCard({ icon, label, amount, color, bg, hint, expandKey, expanded, onToggle, children, debit }: BalanceCardProps) {
  const isOpen = expanded === expandKey;
  return (
    <div style={{ borderRadius: 12, overflow: 'hidden', boxShadow: isOpen ? '0 0 0 2px ' + color : undefined }}>
      <button
        onClick={() => onToggle(isOpen ? null : expandKey)}
        style={{
          ...s.balanceCard, background: bg, width: '100%', cursor: 'pointer',
          border: 'none', textAlign: 'left', outline: 'none',
          borderBottom: isOpen ? `1px solid ${color}44` : undefined,
        }}
      >
        <span style={{ fontSize: 28 }}>{icon}</span>
        <span style={{ fontSize: 11, color: '#6b7280', fontWeight: 600 }}>{label}</span>
        <span style={{ fontSize: 22, fontWeight: 800, color }}>
          {debit ? '-' : ''}{amount < 0 ? '-' : ''}£{Math.abs(amount).toFixed(2)}
        </span>
        <span style={{ fontSize: 10, color: '#9ca3af' }}>{hint}</span>
        {children !== undefined && (
          <span style={{ fontSize: 10, color, fontWeight: 700, marginTop: 2 }}>
            {isOpen ? '▲ Hide' : '▼ Details'}
          </span>
        )}
      </button>
      {isOpen && children && (
        <div style={{ background: bg, padding: '10px 14px', borderTop: `1px dashed ${color}66` }}>
          {children}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ fontWeight: 700, color: color ?? '#111827' }}>{value}</span>
    </div>
  );
}

// ── Monthly Statement Component ──────────────────────────────────────────────

function MonthlyStatement({ client }: { client: ReturnType<typeof createApiClient> }) {
  const now = new Date();
  const [year, setYear]           = useState(now.getFullYear());
  const [month, setMonth]         = useState(now.getMonth() + 1);
  const [stmt, setStmt]           = useState<MonthlyStatementDto | null>(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);

  const load = useCallback(async (y: number, m: number) => {
    setLoading(true);
    setError(null);
    try {
      const data = await paymentService(client).getMonthlyStatement(y, m);
      setStmt(data);
    } catch {
      setError('Failed to load statement. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [client]);

  useEffect(() => { load(year, month); }, [load, year, month]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };
  const isCurrentMonth = year === now.getFullYear() && month === now.getMonth() + 1;

  const fmtDate = (d: string) => new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' });

  // Derived from the statement totals rather than a hardcoded rate, so it always reflects the configured commission.
  const feePercent = stmt && stmt.totalGross > 0 ? Math.round((stmt.totalPlatformFee / stmt.totalGross) * 1000) / 10 : null;

  const statusStyle = (s: string): React.CSSProperties => {
    const map: Record<string, React.CSSProperties> = {
      Released:   { background: '#dcfce7', color: '#166534' },
      Processing: { background: '#dbeafe', color: '#1d4ed8' },
      Pending:    { background: '#fef3c7', color: '#92400e' },
      Failed:     { background: '#fee2e2', color: '#991b1b' },
      Refunded:   { background: '#f3e8ff', color: '#7c3aed' },
      Unpaid:     { background: '#f3f4f6', color: '#374151' },
    };
    return map[s] ?? { background: '#f3f4f6', color: '#374151' };
  };

  return (
    <div>
      {/* Month picker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={prevMonth} style={s.navBtn}>‹</button>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#111827', minWidth: 140, textAlign: 'center' }}>
          {stmt?.monthName ?? new Date(year, month - 1).toLocaleString('en-GB', { month: 'long' })} {year}
        </span>
        <button onClick={nextMonth} disabled={isCurrentMonth} style={{ ...s.navBtn, opacity: isCurrentMonth ? 0.4 : 1 }}>›</button>
        <button onClick={() => load(year, month)} style={s.refreshBtn}>↻ Refresh</button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', borderRadius: 8, padding: '10px 14px', color: '#991b1b', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>Loading statement…</div>
      )}

      {!loading && stmt && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 20 }}>
            {[
              { label: 'Gross Job Value', value: `£${stmt.totalGross.toFixed(2)}`, color: '#1d4ed8', bg: '#eff6ff' },
              { label: `Platform Fee${feePercent != null ? ` (${feePercent}%)` : ''}`, value: `-£${stmt.totalPlatformFee.toFixed(2)}`, color: '#dc2626', bg: '#fee2e2' },
              { label: 'Net Earnings', value: `£${stmt.totalNet.toFixed(2)}`, color: '#166534', bg: '#dcfce7' },
              { label: 'Paid Online', value: `£${stmt.totalOnline.toFixed(2)}`, color: '#1d4ed8', bg: '#eff6ff' },
              { label: 'COD', value: `£${stmt.totalCod.toFixed(2)}`, color: '#92400e', bg: '#fef3c7' },
            ].map(c => (
              <div key={c.label} style={{ background: c.bg, borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 3 }}>
                <span style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{c.label}</span>
                <span style={{ fontSize: 20, fontWeight: 800, color: c.color }}>{c.value}</span>
              </div>
            ))}
          </div>

          {stmt.lines.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#9ca3af' }}>
              No payment activity for {stmt.monthName} {stmt.year}.
            </div>
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ ...s.tableHead, padding: '8px 14px' }}>
                  <span style={{ width: 90 }}>Date</span>
                  <span style={{ width: 110 }}>Job Ref</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Gross</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Online</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>COD</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#dc2626' }}>Platform Fee</span>
                  <span style={{ flex: 1, textAlign: 'right', color: '#166534' }}>Net</span>
                  <span style={{ width: 100, textAlign: 'right' }}>Status</span>
                </div>
                {stmt.lines.map((line, i) => (
                  <div key={line.paymentId || i} style={{ display: 'flex', gap: 8, padding: '10px 14px', alignItems: 'center', borderTop: '1px solid #f3f4f6' }}>
                    <span style={{ width: 90, fontSize: 12, color: '#6b7280' }}>{fmtDate(line.date)}</span>
                    <span style={{ width: 110, fontFamily: 'monospace', fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                      {line.jobReference || line.jobId.slice(0, 8).toUpperCase()}
                    </span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: '#111827' }}>£{line.grossAmount.toFixed(2)}</span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: '#1d4ed8' }}>
                      {line.onlineAmount > 0 ? `£${line.onlineAmount.toFixed(2)}` : '—'}
                    </span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: '#b45309' }}>
                      {line.codAmount > 0 ? `£${line.codAmount.toFixed(2)}` : '—'}
                    </span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: '#dc2626', fontWeight: 700 }}>
                      -£{line.platformFee.toFixed(2)}
                    </span>
                    <span style={{ flex: 1, textAlign: 'right', fontSize: 13, fontWeight: 800, color: '#166534' }}>
                      £{line.netAmount.toFixed(2)}
                    </span>
                    <span style={{ width: 100, textAlign: 'right' }}>
                      <span style={{ ...statusStyle(line.status), fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                        {line.status}
                      </span>
                      {line.codRemittancePending && (
                        <div style={{ fontSize: 9, color: '#b45309', fontWeight: 600, marginTop: 2 }}>⚠ COD due</div>
                      )}
                    </span>
                  </div>
                ))}
              </div>
              <div style={{ padding: '10px 14px', borderTop: '1px solid #f3f4f6', display: 'flex', gap: 8, alignItems: 'center', background: '#f9fafb' }}>
                <span style={{ fontSize: 11, color: '#9ca3af' }}>
                  {stmt.lines.length} payment{stmt.lines.length !== 1 ? 's' : ''} · Platform fee{feePercent != null ? ` is ${feePercent}%` : ''} of gross job value · COD amounts must be remitted to TransitDeal
                </span>
                <button
                  onClick={() => {
                    const rows = [
                      ['Date', 'Job Ref', 'Gross', 'Online', 'COD', `Platform Fee${feePercent != null ? ` (${feePercent}%)` : ''}`, 'Net', 'Status'],
                      ...stmt.lines.map(l => [
                        new Date(l.date).toLocaleDateString('en-GB'),
                        l.jobReference || l.jobId.slice(0, 8),
                        l.grossAmount.toFixed(2),
                        l.onlineAmount.toFixed(2),
                        l.codAmount.toFixed(2),
                        l.platformFee.toFixed(2),
                        l.netAmount.toFixed(2),
                        l.status,
                      ]),
                      ['', 'TOTAL', stmt.totalGross.toFixed(2), stmt.totalOnline.toFixed(2), stmt.totalCod.toFixed(2), stmt.totalPlatformFee.toFixed(2), stmt.totalNet.toFixed(2), ''],
                    ];
                    const csv = rows.map(r => r.join(',')).join('\n');
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
                    a.download = `statement-${stmt.year}-${String(stmt.month).padStart(2, '0')}.csv`;
                    a.click();
                  }}
                  style={{ marginLeft: 'auto', padding: '5px 12px', fontSize: 11, fontWeight: 700, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', cursor: 'pointer' }}
                >
                  ⬇ Export CSV
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────

export default function PaymentsBoardPage() {
  const { getToken, account } = useAppAuth();
  const { isAdminOrOps, isRoleLoading } = useRole();
  const [dashboard, setDashboard] = useState<UserDashboardData | null>(null);
  const [earnings,  setEarnings]  = useState<CarrierEarningsDashboardDto | null>(null);
  const [myJobs,    setMyJobs]    = useState<JobSummaryDto[]>([]);
  const [adminPayments, setAdminPayments] = useState<AdminPaymentsQueueDto | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [reprocessingId, setReprocessingId] = useState<string | null>(null);
  const [reprocessResults, setReprocessResults] = useState<Record<string, PaymentReprocessResult>>({});
  const [expandedCard, setExpandedCard] = useState<ExpandedCard>(null);
  const [earningsKey, setEarningsKey]   = useState<'total' | 'referral' | 'cashback' | 'jobs' | null>(null);
  const [pageTab, setPageTab]           = useState<PageTab>('dashboard');
  const [apiClient, setApiClient] = useState<ReturnType<typeof createApiClient> | null>(null);

  const handleReprocess = useCallback(async (paymentId: string) => {
    setReprocessingId(paymentId);
    try {
      const client = createApiClient(getToken);
      const result = await paymentService(client).reprocessPayment(paymentId);
      setReprocessResults(prev => ({ ...prev, [paymentId]: result }));
    } catch {
      setReprocessResults(prev => ({
        ...prev,
        [paymentId]: {
          paymentId, carrierId: '', earningsPatched: false,
          oldEarnings: 0, newEarnings: 0, notificationSent: false,
          message: 'Request failed — check server logs.',
        },
      }));
    } finally {
      setReprocessingId(null);
    }
  }, [getToken]);

  const load = useCallback(async () => {
    if (!account || isRoleLoading) { setLoading(false); return; }
    const client = createApiClient(getToken);
    setApiClient(client);
    setLoading(true);
    if (isAdminOrOps) {
      await paymentService(client).getAllPayments().then(setAdminPayments).catch(() => {});
    } else {
      await Promise.all([
        client.get<UserDashboardData>('/api/v1/users/dashboard').then(r => setDashboard(r.data)).catch(() => {}),
        paymentService(client).getDashboard().then(setEarnings).catch(() => {}),
        jobService(client).getMyJobs().then(setMyJobs).catch(() => {}),
      ]);
    }
    setLoading(false);
  }, [account, getToken, isAdminOrOps, isRoleLoading]);

  useEffect(() => { load(); }, [load]);

  if (loading || isRoleLoading) return <div style={s.loading}><div style={s.spinner} /></div>;

  const isWorker = ['carrier', 'workshop'].includes(dashboard?.role?.toLowerCase() ?? '');

  const pendingPodJobs  = myJobs.filter(j => j.status === JobStatus.Completed);
  const podVerifiedJobs = myJobs.filter(j => j.status === JobStatus.PodVerified);
  const activeJobs      = myJobs.filter(j => [JobStatus.Active, JobStatus.Quoted, JobStatus.Accepted,
    JobStatus.ContractGenerated, JobStatus.InProgress, JobStatus.CarrierAccepted].includes(j.status));
  const releasedJobs    = myJobs.filter(j => [JobStatus.PaymentReleased, JobStatus.ReviewUnlocked].includes(j.status));

  const avail     = dashboard?.availableBalance   ?? 0;
  const locked    = dashboard?.lockedBalance      ?? 0;
  const pending   = dashboard?.pendingBalance     ?? 0;
  const referral  = dashboard?.referralEarnings   ?? 0;
  const cashback  = dashboard?.cashbackCredit     ?? 0;
  const codTotal  = earnings?.totalCodOutstanding ?? 0;
  const codFee    = earnings?.platformFeeOnCodOwed ?? 0;
  const pendingFee = earnings?.pendingPlatformFee ?? 0;
  const pendingGross = earnings?.pendingGrossAmount ?? 0;

  // Net total: gross balances minus pending platform fee not yet deducted
  const total = avail + locked + pending + referral + cashback - pendingFee;

  const codNet = codTotal - codFee;

  // Derived from actual amounts rather than a hardcoded rate, so it always reflects the configured commission.
  const codFeePercent     = codTotal > 0 ? Math.round((codFee / codTotal) * 1000) / 10 : null;
  const pendingFeePercent = pendingGross > 0 ? Math.round((pendingFee / pendingGross) * 1000) / 10 : null;

  // ── Admin view ──────────────────────────────────────────────────────────
  if (isAdminOrOps) {
    const pmts = adminPayments?.payments ?? [];
    return (
      <div style={s.page}>
        <div style={s.header}>
          <div>
            <h1 style={s.h1}>All Payments (Admin)</h1>
            <p style={s.subtitle}>Platform-wide view of all payments across all users</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <a href="/admin/payment-reconciliation"
              style={{ padding: '8px 14px', border: '1px solid #7c3aed', borderRadius: 8, background: '#f5f3ff', color: '#7c3aed', cursor: 'pointer', fontSize: 12, fontWeight: 700, textDecoration: 'none' }}>
              ⚡ Reconciliation
            </a>
            <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
          </div>
        </div>

        <div style={s.summaryRow}>
          {[
            { label: 'Total Payments', value: adminPayments?.totalCount ?? 0, color: '#374151' },
            { label: 'Total Net Value', value: `£${(adminPayments?.totalNetAmount ?? 0).toFixed(2)}`, color: '#166534' },
          ].map(stat => (
            <div key={stat.label} style={s.statCard}>
              <span style={{ fontSize: 22, fontWeight: 800, color: stat.color }}>{stat.value}</span>
              <span style={s.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>

        <div style={s.section}>
          <div style={{ overflowX: 'auto' }}>
            <div style={s.tableHead}>
              <span style={{ width: 110 }}>Job Reference</span>
              <span style={{ width: 100 }}>Created</span>
              <span style={{ width: 100 }}>Released</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Gross</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Commission</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Net</span>
              <span style={{ width: 100, textAlign: 'right' }}>Status</span>
              <span style={{ width: 90, textAlign: 'right' }}>Action</span>
            </div>
            {pmts.length === 0 && (
              <p style={{ textAlign: 'center', color: '#9ca3af', padding: '24px 0' }}>No payments on the platform yet.</p>
            )}
            {pmts.map(p => {
              const sc = PAY_STATUS_STYLE[p.status] ?? { background: '#f3f4f6', color: '#374151' };
              const isReleased = p.status === PaymentStatus.Released;
              const isReprocessing = reprocessingId === p.id;
              const result = reprocessResults[p.id];
              return (
                <div key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', gap: 8, padding: '10px', alignItems: 'center' }}>
                    <a href={`/jobs/${p.jobId}`} style={{ display: 'contents', textDecoration: 'none', color: 'inherit' }}>
                      <span style={{ width: 110, fontFamily: 'monospace', fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                        {p.jobId.slice(0, 8)}
                      </span>
                      <span style={{ width: 100, fontSize: 12, color: '#6b7280' }}>
                        {new Date(p.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })}
                      </span>
                      <span style={{ width: 100, fontSize: 12, color: '#6b7280' }}>
                        {p.releasedAt ? new Date(p.releasedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                      </span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: '#111827' }}>£{p.grossAmount.toFixed(2)}</span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: '#dc2626' }}>
                        {p.commissionDeducted > 0 ? `-£${p.commissionDeducted.toFixed(2)}` : '—'}
                      </span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 13, fontWeight: 800, color: '#166534' }}>£{p.netAmount.toFixed(2)}</span>
                      <span style={{ width: 100, textAlign: 'right' }}>
                        <span style={{ ...sc, fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                          {PAY_STATUS_LABELS[p.status] ?? 'Unknown'}
                        </span>
                      </span>
                    </a>
                    <span style={{ width: 90, textAlign: 'right' }}>
                      {isReleased && (
                        <button
                          onClick={() => handleReprocess(p.id)}
                          disabled={isReprocessing}
                          style={{
                            padding: '3px 10px', fontSize: 11, fontWeight: 700, borderRadius: 6,
                            border: '1px solid #7c3aed', background: isReprocessing ? '#ede9fe' : '#f5f3ff',
                            color: '#7c3aed', cursor: isReprocessing ? 'not-allowed' : 'pointer',
                            opacity: isReprocessing ? 0.7 : 1,
                          }}
                        >
                          {isReprocessing ? '…' : '⚡ Fix'}
                        </button>
                      )}
                    </span>
                  </div>
                  {result && (
                    <div style={{
                      margin: '0 10px 8px', padding: '8px 12px', borderRadius: 6, fontSize: 11,
                      background: result.earningsPatched ? '#f0fdf4' : '#eff6ff',
                      border: `1px solid ${result.earningsPatched ? '#bbf7d0' : '#bfdbfe'}`,
                      color: result.earningsPatched ? '#15803d' : '#1d4ed8',
                      display: 'flex', gap: 8, alignItems: 'center',
                    }}>
                      <span>{result.earningsPatched ? '✅' : 'ℹ️'}</span>
                      <span style={{ fontWeight: 600 }}>{result.message}</span>
                      {result.earningsPatched && (
                        <span style={{ color: '#6b7280' }}>
                          £{result.oldEarnings.toFixed(2)} → £{result.newEarnings.toFixed(2)}
                        </span>
                      )}
                      {result.notificationSent && <span style={{ color: '#6b7280' }}>· Notified ✓</span>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ── Carrier / Workshop / Customer view ──────────────────────────────────
  return (
    <div style={s.page}>
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Payments Board</h1>
          <p style={s.subtitle}>Your complete financial overview — click any card to see the breakdown</p>
        </div>
        <button onClick={load} style={s.refreshBtn}>↻ Refresh</button>
      </div>

      {/* Page-level tabs */}
      <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', marginBottom: 24, gap: 0 }}>
        {([['dashboard', '📊 Dashboard'], ['statement', '📋 Monthly Statement']] as [PageTab, string][]).map(([key, label]) => (
          <button key={key} onClick={() => setPageTab(key)} style={{
            padding: '10px 20px', border: 'none', background: 'transparent', fontFamily: 'inherit',
            color: pageTab === key ? '#047857' : '#6b7280',
            fontWeight: pageTab === key ? 700 : 400,
            borderBottom: pageTab === key ? '2px solid #047857' : '2px solid transparent',
            fontSize: 13, cursor: 'pointer',
          }}>{label}</button>
        ))}
      </div>

      {/* ── Monthly Statement tab ── */}
      {pageTab === 'statement' && apiClient && (
        <MonthlyStatement client={apiClient} />
      )}

      {/* ── Dashboard tab ── */}
      {pageTab === 'dashboard' && (
        <>
          {/* Total portfolio */}
          <div style={s.totalCard}>
            <span style={{ fontSize: 13, color: '#a7f3d0', fontWeight: 500 }}>Total Portfolio Value (Net)</span>
            <span style={{ fontSize: 38, fontWeight: 800, color: '#fff', lineHeight: 1.1 }}>
              £{total.toFixed(2)}
            </span>
            <span style={{ fontSize: 12, color: '#6ee7b7' }}>
              Gross balances minus pending platform fee
              {pendingFee > 0 && ` (pending fee: £${pendingFee.toFixed(2)})`}
            </span>
          </div>

          {/* Balance cards */}
          <div style={s.balanceGrid}>
            <BalanceCard
              icon="✅" label="Available Funds" amount={avail}
              color="#166534" bg="#dcfce7" hint="Ready to withdraw"
              expandKey="available" expanded={expandedCard} onToggle={setExpandedCard}
            >
              <DetailRow label="Released earnings" value={`£${avail.toFixed(2)}`} color="#166534" />
              <DetailRow label="Cashback applied"  value={`£${cashback.toFixed(2)}`} color="#0f766e" />
              {releasedJobs.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px dashed #86efac', paddingTop: 8 }}>
                  <p style={{ fontSize: 10, color: '#166534', fontWeight: 700, margin: '0 0 6px' }}>
                    {releasedJobs.length} released job{releasedJobs.length !== 1 ? 's' : ''}
                  </p>
                  {releasedJobs.slice(0, 5).map(job => (
                    <a key={job.id} href={`/jobs/${job.id}`} style={s.miniJobRow}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f0fdf4')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={s.refNo}>{job.referenceNumber}</span>
                      <span style={{ flex: 1, fontSize: 12, color: '#111827', fontWeight: 600 }}>{job.title}</span>
                      <span style={s.viewLink}>View →</span>
                    </a>
                  ))}
                </div>
              )}
              {releasedJobs.length === 0 && (
                <p style={{ fontSize: 10, color: '#6b7280', margin: '6px 0 0' }}>
                  Available funds are ready to withdraw to your bank account at any time.
                </p>
              )}
            </BalanceCard>

            <BalanceCard
              icon="🔒" label="Locked (Deposits)" amount={locked}
              color="#1d4ed8" bg="#dbeafe" hint="Security deposits"
              expandKey="locked" expanded={expandedCard} onToggle={setExpandedCard}
            >
              <DetailRow label="Active jobs with deposit" value={String(activeJobs.length)} />
              <DetailRow label="Total locked"             value={`£${locked.toFixed(2)}`} color="#1d4ed8" />
              {activeJobs.length > 0 && (
                <div style={{ marginTop: 8, borderTop: '1px dashed #93c5fd', paddingTop: 8 }}>
                  {activeJobs.slice(0, 5).map(job => (
                    <a key={job.id} href={`/jobs/${job.id}`} style={s.miniJobRow}
                      onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={s.refNo}>{job.referenceNumber}</span>
                      <span style={{ flex: 1, fontSize: 12, color: '#111827', fontWeight: 600 }}>{job.title}</span>
                      <span style={{ ...s.viewLink, color: '#1d4ed8' }}>View →</span>
                    </a>
                  ))}
                </div>
              )}
              {activeJobs.length === 0 && (
                <p style={{ fontSize: 10, color: '#6b7280', margin: '6px 0 0' }}>
                  Security deposits are held until jobs are completed and verified.
                </p>
              )}
            </BalanceCard>

            <BalanceCard
              icon="⏳" label="Pending Release" amount={pending}
              color="#92400e" bg="#fef3c7" hint="Awaiting POD verification"
              expandKey="pending" expanded={expandedCard} onToggle={setExpandedCard}
            >
              <DetailRow label="Gross job value (pending)" value={`£${pendingGross.toFixed(2)}`} />
              <DetailRow label="Platform fee on pending"   value={`-£${pendingFee.toFixed(2)}`} color="#dc2626" />
              <DetailRow label="Net to you (pending)"      value={`£${pending.toFixed(2)}`} color="#92400e" />
              <DetailRow label="Jobs awaiting POD"         value={String(pendingPodJobs.length)} />
              <DetailRow label="POD submitted (in review)" value={String(podVerifiedJobs.length)} />
              {(pendingPodJobs.length > 0 || podVerifiedJobs.length > 0) && (
                <div style={{ marginTop: 8, borderTop: '1px dashed #fcd34d', paddingTop: 8 }}>
                  {[...pendingPodJobs, ...podVerifiedJobs].slice(0, 6).map(job => (
                    <a key={job.id} href={`/jobs/${job.id}`} style={s.miniJobRow}
                      onMouseEnter={e => (e.currentTarget.style.background = '#fffbeb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={s.refNo}>{job.referenceNumber}</span>
                      <span style={{ flex: 1, fontSize: 12, color: '#111827', fontWeight: 600 }}>{job.title}</span>
                      <span style={{ ...s.viewLink, color: '#92400e' }}>View →</span>
                    </a>
                  ))}
                </div>
              )}
              {pendingPodJobs.length === 0 && podVerifiedJobs.length === 0 && (
                <p style={{ fontSize: 10, color: '#6b7280', margin: '6px 0 0' }}>
                  Released once POD is verified. Platform fee{pendingFeePercent != null ? ` (${pendingFeePercent}%)` : ''} applies to gross job value.
                </p>
              )}
            </BalanceCard>

            <BalanceCard
              icon="👥" label="Referral Earnings" amount={referral}
              color="#7c3aed" bg="#f3e8ff" hint="From referred users"
              expandKey="referral" expanded={expandedCard} onToggle={setExpandedCard}
            >
              <DetailRow label="Total referral earned" value={`£${referral.toFixed(2)}`} color="#7c3aed" />
              <p style={{ fontSize: 10, color: '#6b7280', margin: '6px 0 0' }}>
                1% of every job your referred users complete, for 12 months.
              </p>
            </BalanceCard>

            <BalanceCard
              icon="🎁" label="Cashback Credit" amount={cashback}
              color="#0f766e" bg="#ccfbf1" hint="Applied on next job"
              expandKey="cashback" expanded={expandedCard} onToggle={setExpandedCard}
            >
              <DetailRow label="Cashback available" value={`£${cashback.toFixed(2)}`} color="#0f766e" />
              <p style={{ fontSize: 10, color: '#6b7280', margin: '6px 0 0' }}>
                5% cashback on your first 3 customer jobs. Auto-applied on your next payment.
              </p>
            </BalanceCard>

            {/* Pending Platform Fee — shown as a debit card only when there are pending payments */}
            {pendingFee > 0 && (
              <BalanceCard
                icon="🏛" label="Pending Platform Fee" amount={pendingFee}
                color="#dc2626" bg="#fee2e2" hint={pendingFeePercent != null ? `${pendingFeePercent}% of pending job value` : 'Platform fee on pending job value'}
                expandKey="pendingFee" expanded={expandedCard} onToggle={setExpandedCard}
                debit
              >
                <DetailRow label="Pending gross"    value={`£${pendingGross.toFixed(2)}`} />
                <DetailRow label="Fee rate"         value={pendingFeePercent != null ? `${pendingFeePercent}%` : '—'} />
                <DetailRow label="Fee due"          value={`£${pendingFee.toFixed(2)}`} color="#dc2626" />
                <p style={{ fontSize: 10, color: '#6b7280', margin: '6px 0 0' }}>
                  Platform fee on pending/processing payments. Deducted automatically on release for online payments; must be remitted for COD.
                </p>
              </BalanceCard>
            )}
          </div>

          {/* Fee transparency notice */}
          {pendingFee > 0 && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 12 }}>
              <span style={{ fontSize: 16 }}>ℹ️</span>
              <div>
                <span style={{ fontWeight: 700, color: '#92400e' }}>Fee Transparency: </span>
                <span style={{ color: '#78350f' }}>
                  TransitDeal charges a flat platform fee{pendingFeePercent != null ? ` (${pendingFeePercent}%)` : ''} on the gross job value (£{pendingGross.toFixed(2)} pending).
                  For online payments this is deducted automatically. For COD (£{codTotal.toFixed(2)}), you collect from the customer and remit £{codFee.toFixed(2)} to TransitDeal.
                  No other deductions apply.
                </span>
              </div>
            </div>
          )}

          {/* COD section — only shown when there is active COD */}
          {codTotal > 0 && (
            <div style={{ ...s.section, borderLeft: '4px solid #f59e0b', marginBottom: 20 }}>
              <h2 style={{ ...s.sectionTitle, color: '#92400e' }}>
                💵 Cash on Delivery (COD) Overview
              </h2>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10, marginBottom: 12 }}>
                <button
                  onClick={() => setExpandedCard(expandedCard === 'cod' ? null : 'cod')}
                  style={{ ...s.codCard, background: '#fffbeb', borderColor: '#f59e0b', textAlign: 'left', cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 11, color: '#92400e', fontWeight: 700 }}>COD TO COLLECT (GROSS)</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#b45309' }}>£{codTotal.toFixed(2)}</span>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>Full amount from customer</span>
                  <span style={{ fontSize: 10, color: '#b45309', fontWeight: 700 }}>
                    {expandedCard === 'cod' ? '▲ Hide' : '▼ Details'}
                  </span>
                </button>

                <button
                  onClick={() => setExpandedCard(expandedCard === 'platformFee' ? null : 'platformFee')}
                  style={{ ...s.codCard, background: '#fee2e2', borderColor: '#dc2626', textAlign: 'left', cursor: 'pointer' }}
                >
                  <span style={{ fontSize: 11, color: '#991b1b', fontWeight: 700 }}>PLATFORM FEE ON COD</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#dc2626' }}>£{codFee.toFixed(2)}</span>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>{codFeePercent != null ? `${codFeePercent}% — ` : ''}owed to TransitDeal</span>
                  <span style={{ fontSize: 10, color: '#dc2626', fontWeight: 700 }}>
                    {expandedCard === 'platformFee' ? '▲ Hide' : '▼ Details'}
                  </span>
                </button>

                <div style={{ ...s.codCard, background: '#f0fdf4', borderColor: '#16a34a' }}>
                  <span style={{ fontSize: 11, color: '#166534', fontWeight: 700 }}>YOUR NET COD</span>
                  <span style={{ fontSize: 22, fontWeight: 800, color: '#16a34a' }}>£{codNet.toFixed(2)}</span>
                  <span style={{ fontSize: 10, color: '#9ca3af' }}>After{codFeePercent != null ? ` ${codFeePercent}%` : ''} platform fee</span>
                </div>
              </div>

              {expandedCard === 'cod' && (
                <div style={s.expandBox}>
                  <p style={{ fontWeight: 700, color: '#92400e', fontSize: 13, marginBottom: 8 }}>COD Breakdown</p>
                  {earnings?.recentPayments
                    .filter(p => (p.codAmount ?? 0) > 0 &&
                      (p.status === PaymentStatus.Pending || p.status === PaymentStatus.Processing))
                    .map(p => (
                      <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderBottom: '1px solid #fde68a' }}>
                        <span style={{ color: '#374151' }}>{p.jobReference || p.jobId.slice(0, 8)}</span>
                        <span style={{ fontWeight: 700, color: '#b45309' }}>£{(p.codAmount ?? 0).toFixed(2)} gross</span>
                      </div>
                    ))}
                  <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                    Collect these amounts from the customer at delivery. Remit £{codFee.toFixed(2)}{codFeePercent != null ? ` (${codFeePercent}%)` : ''} to TransitDeal.
                  </p>
                </div>
              )}

              {expandedCard === 'platformFee' && (
                <div style={{ ...s.expandBox, background: '#fff5f5', borderColor: '#fca5a5' }}>
                  <p style={{ fontWeight: 700, color: '#991b1b', fontSize: 13, marginBottom: 8 }}>Platform Fee Due on COD</p>
                  {earnings?.recentPayments
                    .filter(p => (p.codAmount ?? 0) > 0 &&
                      (p.status === PaymentStatus.Pending || p.status === PaymentStatus.Processing))
                    .map(p => {
                      const fee = Math.round((p.codAmount ?? 0) * ((codFeePercent ?? 0) / 100) * 100) / 100;
                      return (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '6px 0', borderBottom: '1px solid #fca5a5' }}>
                          <span style={{ color: '#374151' }}>{p.jobReference || p.jobId.slice(0, 8)}</span>
                          <span style={{ fontWeight: 700, color: '#dc2626' }}>£{fee.toFixed(2)}</span>
                        </div>
                      );
                    })}
                  <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
                    Platform fee{codFeePercent != null ? ` (${codFeePercent}%)` : ''} on COD. Remit to TransitDeal after collecting from customer. No other deductions apply.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Pending POD jobs */}
          {pendingPodJobs.length > 0 && (
            <div style={{ ...s.section, borderLeft: '4px solid #f97316' }}>
              <h2 style={s.sectionTitle}>
                ⏳ Pending POD Jobs — funds locked until verified
                <span style={s.countBadge}>{pendingPodJobs.length}</span>
              </h2>
              <div style={s.jobMiniTable}>
                {pendingPodJobs.map(job => (
                  <a key={job.id} href={`/jobs/${job.id}`} style={s.miniJobRow}
                    onMouseEnter={e => (e.currentTarget.style.background = '#fff7ed')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={s.refNo}>{job.referenceNumber}</span>
                    <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontSize: 13 }}>{job.title}</span>
                    <span style={s.viewLink}>View Job →</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* POD submitted — awaiting payment release */}
          {podVerifiedJobs.length > 0 && (
            <div style={{ ...s.section, borderLeft: '4px solid #8b5cf6' }}>
              <h2 style={s.sectionTitle}>
                🔄 POD Verified — payment processing
                <span style={s.countBadge}>{podVerifiedJobs.length}</span>
              </h2>
              <div style={s.jobMiniTable}>
                {podVerifiedJobs.map(job => (
                  <a key={job.id} href={`/jobs/${job.id}`} style={s.miniJobRow}
                    onMouseEnter={e => (e.currentTarget.style.background = '#faf5ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={s.refNo}>{job.referenceNumber}</span>
                    <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontSize: 13 }}>{job.title}</span>
                    <span style={s.viewLink}>View Job →</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Active jobs with locked deposits */}
          {activeJobs.length > 0 && (
            <div style={{ ...s.section, borderLeft: '4px solid #3b82f6' }}>
              <h2 style={s.sectionTitle}>
                📦 Active Jobs with Locked Deposits
                <span style={s.countBadge}>{activeJobs.length}</span>
              </h2>
              <div style={s.jobMiniTable}>
                {activeJobs.map(job => (
                  <a key={job.id} href={`/jobs/${job.id}`} style={s.miniJobRow}
                    onMouseEnter={e => (e.currentTarget.style.background = '#eff6ff')}
                    onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                  >
                    <span style={s.refNo}>{job.referenceNumber}</span>
                    <span style={{ flex: 1, fontWeight: 600, color: '#111827', fontSize: 13 }}>{job.title}</span>
                    <span style={{ fontSize: 11, color: '#3b82f6', padding: '2px 8px', background: '#dbeafe', borderRadius: 12, fontWeight: 600 }}>
                      {JobStatus[job.status]}
                    </span>
                    <span style={s.viewLink}>View Job →</span>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Carrier/Workshop earnings summary */}
          {isWorker && earnings && (() => {
            const earningItems: { key: typeof earningsKey; label: string; value: string; color: string; hasPayments: boolean }[] = [
              { key: 'total',    label: 'Total Earned',    value: `£${(earnings.totalEarnings    ?? 0).toFixed(2)}`, color: '#166534', hasPayments: true  },
              { key: 'referral', label: 'Referral Earned', value: `£${(earnings.referralEarnings ?? 0).toFixed(2)}`, color: '#7c3aed', hasPayments: false },
              { key: 'cashback', label: 'Cashback Wallet', value: `£${(earnings.cashbackWallet   ?? 0).toFixed(2)}`, color: '#0f766e', hasPayments: false },
              { key: 'jobs',     label: 'Jobs Completed',  value: String(earnings.completedJobs  ?? 0),              color: '#374151', hasPayments: false },
            ];
            const activeItem = earningItems.find(i => i.key === earningsKey) ?? null;
            const drillPayments = earningsKey === 'total' ? (earnings.recentPayments ?? []) : [];
            return (
              <div style={s.section}>
                <h2 style={s.sectionTitle}>Earnings Summary</h2>
                <div style={s.earningsGrid}>
                  {earningItems.map(item => {
                    const isOpen = earningsKey === item.key;
                    return (
                      <button
                        key={item.key as string}
                        onClick={() => setEarningsKey(isOpen ? null : item.key)}
                        style={{
                          ...s.earningCard,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          outline: 'none',
                          border: isOpen ? `2px solid ${item.color}` : '2px solid transparent',
                          textAlign: 'left' as const,
                        }}
                      >
                        <span style={{ fontSize: 10, color: '#9ca3af', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: 0.4 }}>{item.label}</span>
                        <span style={{ fontSize: 22, fontWeight: 800, color: item.color }}>{item.value}</span>
                        <span style={{ fontSize: 9, color: item.color, fontWeight: 700, marginTop: 2 }}>
                          {isOpen ? '▲ Hide' : '▼ Details'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                {activeItem?.hasPayments && drillPayments.length > 0 && (
                  <div style={{ marginTop: 14, borderTop: '1px solid #f3f4f6', paddingTop: 12 }}>
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '0 0 8px', fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.4 }}>
                      {activeItem.label} — {drillPayments.length} payment{drillPayments.length !== 1 ? 's' : ''}
                    </p>
                    {drillPayments.slice(0, 8).map(p => (
                      <a key={p.id} href={`/jobs/${p.jobId}`} style={s.miniJobRow}
                        onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                      >
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#1d4ed8', fontWeight: 600, minWidth: 100 }}>
                          {p.jobReference || p.jobId?.slice(0, 8)}
                        </span>
                        <span style={{ fontSize: 11, color: '#6b7280', minWidth: 80 }}>
                          {p.releasedAt ? new Date(p.releasedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : '—'}
                        </span>
                        <span style={{ flex: 1 }} />
                        <span style={{ fontSize: 13, fontWeight: 800, color: '#166534' }}>£{p.netAmount.toFixed(2)}</span>
                        <span style={{ ...s.viewLink, marginLeft: 12 }}>View →</span>
                      </a>
                    ))}
                  </div>
                )}
                {activeItem && !activeItem.hasPayments && (
                  <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
                    {earningsKey === 'referral'
                      ? 'Referral earnings come from referred users completing jobs. See the Referrals Board for a full breakdown.'
                      : earningsKey === 'cashback'
                      ? 'Cashback credit is applied automatically on your next payment checkout.'
                      : `${activeItem.value} jobs completed in total.`}
                  </p>
                )}
              </div>
            );
          })()}

          {/* Recent payments */}
          {earnings?.recentPayments && earnings.recentPayments.length > 0 && (
            <div style={s.section}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <h2 style={{ ...s.sectionTitle, margin: 0 }}>Recent Payments</h2>
                <button
                  onClick={() => setPageTab('statement')}
                  style={{ fontSize: 13, color: '#047857', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}
                >
                  View monthly statement →
                </button>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ ...s.tableHead }}>
                  <span style={{ width: 110 }}>Job Reference</span>
                  <span style={{ width: 100 }}>Released</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Gross</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Online</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>COD</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Platform Fee</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>Net to You</span>
                  <span style={{ width: 100, textAlign: 'right' }}>Status</span>
                </div>
                {earnings.recentPayments.map(p => {
                  const sc = PAY_STATUS_STYLE[p.status] ?? { background: '#f3f4f6', color: '#374151' };
                  const hasSplit = (p.onlineAmount != null || p.codAmount != null);
                  return (
                    <a key={p.id} href={`/jobs/${p.jobId}`} style={{ ...s.payRow, textDecoration: 'none', color: 'inherit' }}
                      onMouseEnter={e => (e.currentTarget.style.background = '#f9fafb')}
                      onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ width: 110, fontFamily: 'monospace', fontSize: 12, color: '#1d4ed8', fontWeight: 600 }}>
                        {p.jobReference || p.jobId?.slice(0, 8) || ''}
                      </span>
                      <span style={{ width: 100, fontSize: 12, color: '#6b7280' }}>
                        {p.releasedAt
                          ? new Date(p.releasedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' })
                          : ''}
                      </span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 13, color: '#111827' }}>£{p.grossAmount.toFixed(2)}</span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: '#1d4ed8' }}>
                        {hasSplit ? `£${(p.onlineAmount ?? 0).toFixed(2)}` : '—'}
                      </span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: '#b45309' }}>
                        {hasSplit ? `£${(p.codAmount ?? 0).toFixed(2)}` : '—'}
                      </span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 12, color: '#dc2626' }}>
                        {p.commissionDeducted > 0 ? `-£${p.commissionDeducted.toFixed(2)}` : '—'}
                      </span>
                      <span style={{ flex: 1, textAlign: 'right', fontSize: 13, fontWeight: 800, color: '#166534' }}>£{p.netAmount.toFixed(2)}</span>
                      <span style={{ width: 100, textAlign: 'right' }}>
                        <span style={{ ...sc, fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                          {PAY_STATUS_LABELS[p.status] ?? 'Unknown'}
                        </span>
                      </span>
                    </a>
                  );
                })}
              </div>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, margin: '10px 0 0' }}>
                Click any row to view the source job · Platform fee shown separately — never deducted from COD gross
              </p>
            </div>
          )}

          {/* Quick actions */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button onClick={() => setPageTab('statement')} style={s.actionBtn}>Monthly Statement →</button>
            <a href="/my-dashboard" style={{ ...s.actionBtn, background: '#fff', color: '#374151', border: '1px solid #d1d5db', textDecoration: 'none' }}>My Dashboard →</a>
            <a href="/dashboards/jobs" style={{ ...s.actionBtn, background: '#fff', color: '#374151', border: '1px solid #d1d5db', textDecoration: 'none' }}>Jobs Board →</a>
          </div>
        </>
      )}
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page:          { width: '100%', padding: '24px 16px' },
  loading:       { minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner:       { width: 36, height: 36, border: '3px solid #e5e7eb', borderTop: '3px solid #047857', borderRadius: '50%' },
  header:        { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  h1:            { fontSize: 24, fontWeight: 800, color: '#111827', margin: 0 },
  subtitle:      { fontSize: 13, color: '#6b7280', margin: '4px 0 0' },
  refreshBtn:    { padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13, fontFamily: 'inherit' },
  navBtn:        { padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: 8, background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 16, fontFamily: 'inherit', fontWeight: 700 },
  totalCard:     { background: 'linear-gradient(135deg, #047857 0%, #065f46 100%)', borderRadius: 16, padding: '24px 28px', marginBottom: 24, display: 'flex', flexDirection: 'column' as const, gap: 4 },
  balanceGrid:   { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 },
  balanceCard:   { borderRadius: 12, padding: '16px', display: 'flex', flexDirection: 'column' as const, gap: 6 },
  codCard:       { borderRadius: 12, padding: '14px 16px', display: 'flex', flexDirection: 'column' as const, gap: 5, border: '1px solid', outline: 'none' },
  expandBox:     { background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '12px 14px', marginTop: 8 },
  section:       { background: '#fff', borderRadius: 12, padding: 20, marginBottom: 20, boxShadow: '0 1px 6px rgba(0,0,0,0.06)' },
  sectionTitle:  { fontSize: 16, fontWeight: 700, color: '#111827', margin: '0 0 14px', display: 'flex', alignItems: 'center', gap: 8 },
  countBadge:    { fontSize: 12, background: '#f3f4f6', color: '#374151', padding: '2px 8px', borderRadius: 12, fontWeight: 600 },
  jobMiniTable:  { display: 'flex', flexDirection: 'column' as const, gap: 0 },
  miniJobRow:    { display: 'flex', gap: 10, alignItems: 'center', padding: '10px', borderRadius: 8, textDecoration: 'none', color: 'inherit', borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s', cursor: 'pointer' },
  refNo:         { fontFamily: 'monospace', fontSize: 11, color: '#6b7280', minWidth: 100 },
  viewLink:      { fontSize: 12, color: '#047857', fontWeight: 700, whiteSpace: 'nowrap' as const },
  earningsGrid:  { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 },
  earningCard:   { background: '#f9fafb', borderRadius: 8, padding: '12px 14px', display: 'flex', flexDirection: 'column' as const, gap: 4 },
  tableHead:     { display: 'flex', gap: 8, padding: '5px 10px', fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 2 },
  payRow:        { display: 'flex', gap: 8, padding: '10px', borderRadius: 8, alignItems: 'center', borderBottom: '1px solid #f3f4f6', transition: 'background 0.12s', cursor: 'pointer' },
  actionBtn:     { padding: '10px 20px', background: '#047857', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer', fontFamily: 'inherit' },
  summaryRow:    { display: 'flex', gap: 10, marginBottom: 24, flexWrap: 'wrap' as const },
  statCard:      { background: '#fff', borderRadius: 10, padding: '12px 16px', display: 'flex', flexDirection: 'column' as const, alignItems: 'center', gap: 3, boxShadow: '0 1px 6px rgba(0,0,0,0.06)', flex: 1, minWidth: 90 },
  statLabel:     { fontSize: 10, color: '#9ca3af', textAlign: 'center' as const },
};
